import * as React from "react"
import type { ReactElement, KeyboardEvent } from "react"
import { Check } from "lucide-react"

import {
  RefreshCCWIcon,
  type RefreshCCWIconHandle,
} from "@/components/ui/refresh-ccw-icon"
import { UploadIcon, type UploadIconHandle } from "@/components/ui/upload-icon"
import { cn } from "@/lib/utils"
import type { ConnectBrokerImportMethodId } from "@/views/Dashboard/Profile/connectBroker/connectBrokerImportMethods"

const TITLE_BASE =
  "font-[family-name:var(--font-primary)] text-sm font-medium"

const DESC_BASE =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white"

const ICON_CLASS_DEFAULT = "shrink-0 text-gray-500 dark:text-white"
const ICON_CLASS_SELECTED = "shrink-0 text-[color:var(--brand-tropical-surf)]"

export type ConnectBrokerImportMethodCardIcon = "auto_sync" | "file_upload"

export type ConnectBrokerImportMethodCardProps = {
  /** Import method id, or a lane slug (e.g. tradingview_paper) for TradingView path cards. */
  methodId: ConnectBrokerImportMethodId | string
  title: string
  description: string
  selected?: boolean
  onSelect?: () => void
  /**
   * When false, card is display-only (e.g. locked File upload under TradingView).
   * Defaults to true when `onSelect` is provided.
   */
  interactive?: boolean
  /** Defaults to sync icon for auto_sync, upload icon otherwise. */
  icon?: ConnectBrokerImportMethodCardIcon
}

export function ConnectBrokerImportMethodCard({
  methodId,
  title,
  description,
  selected = false,
  onSelect,
  interactive,
  icon,
}: ConnectBrokerImportMethodCardProps): ReactElement {
  const syncIconRef = React.useRef<RefreshCCWIconHandle | null>(null)
  const uploadIconRef = React.useRef<UploadIconHandle | null>(null)
  const resolvedIcon: ConnectBrokerImportMethodCardIcon =
    icon ?? (methodId === "auto_sync" ? "auto_sync" : "file_upload")
  const isInteractive = interactive ?? Boolean(onSelect)

  const startIconAnimation = React.useCallback(() => {
    if (resolvedIcon === "auto_sync") {
      void syncIconRef.current?.startAnimation()
      return
    }
    void uploadIconRef.current?.startAnimation()
  }, [resolvedIcon])

  const stopIconAnimation = React.useCallback(() => {
    if (resolvedIcon === "auto_sync") {
      void syncIconRef.current?.stopAnimation()
      return
    }
    void uploadIconRef.current?.stopAnimation()
  }, [resolvedIcon])

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive || !onSelect) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      onSelect()
    }
  }

  return (
    <div
      role={isInteractive ? "button" : "group"}
      tabIndex={isInteractive ? 0 : undefined}
      data-connect-import-method-card=""
      data-method={methodId}
      data-selected={selected ? "true" : "false"}
      data-interactive={isInteractive ? "true" : "false"}
      aria-pressed={isInteractive ? selected : undefined}
      aria-disabled={isInteractive ? undefined : true}
      aria-label={`${title}. ${description}`}
      onClick={isInteractive && onSelect ? onSelect : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      onPointerEnter={startIconAnimation}
      onPointerLeave={stopIconAnimation}
      onFocus={isInteractive ? startIconAnimation : undefined}
      onBlur={isInteractive ? stopIconAnimation : undefined}
      className={cn(
        "relative flex min-h-[7.5rem] w-full flex-col items-center justify-center gap-2 rounded-[6px] p-4 text-center outline-none",
        isInteractive &&
          "focus-visible:ring-2 focus-visible:ring-[color:var(--brand-tropical-surf)]/40",
        !isInteractive && "cursor-default"
      )}
    >
      {selected ? (
        <span
          className="absolute top-2.5 right-2.5 flex size-5 items-center justify-center rounded-full bg-[color:var(--brand-tropical-surf)] text-white"
          aria-hidden
        >
          <Check className="size-3" />
        </span>
      ) : null}
      <span
        className={cn(
          "flex size-11 shrink-0 items-center justify-center rounded-[6px] border border-border bg-muted/30",
          selected && "border-[color:var(--brand-tropical-surf)]/30 bg-[color:var(--brand-tropical-surf)]/10"
        )}
        aria-hidden
      >
        {resolvedIcon === "auto_sync" ? (
          <RefreshCCWIcon
            ref={syncIconRef}
            size={22}
            className={selected ? ICON_CLASS_SELECTED : ICON_CLASS_DEFAULT}
          />
        ) : (
          <UploadIcon
            ref={uploadIconRef}
            size={22}
            className={selected ? ICON_CLASS_SELECTED : ICON_CLASS_DEFAULT}
          />
        )}
      </span>
      <span
        className={cn(
          TITLE_BASE,
          selected
            ? "text-[color:var(--brand-tropical-surf)]"
            : "text-gray-500 dark:text-white"
        )}
      >
        {title}
      </span>
      <span className={cn(DESC_BASE, "max-w-[16rem]")}>{description}</span>
    </div>
  )
}
